import { css } from '@emotion/react'
import { HFlow, Icon, Theme, useTheme, VFlow } from 'bold-ui'
import { createDecorator, Form, FormDebouncedValueSpy, SwitchField, TextField } from 'components/form'
import { MAX_LIMIT_NOTIFICATION_COUNT_DEFAULT } from 'components/notificacao/useBadgeConfig'
import { PopperButton, PopperControls } from 'components/popper'
import { FormState } from 'final-form'
import { TipoTopicoNotificacaoEnum } from 'graphql/types.generated'
import { useTopicoNotificacaoFiltered } from 'hooks/notificacao/useTopicoNotificacaoFiltered'
import { useFilterCounter } from 'hooks/useFilterCounter'
import { Fragment, useCallback } from 'react'
import { metaPath } from 'util/metaPath'

import { listaRegulacaoTableFilterCalculator } from '../calculator-regulacao'
import {
  LISTA_REGULACAO_DEFAULT_FILTERS,
  ListaRegulacaoFilterModel,
  ListaRegulacaoTableFilterModel,
  ListaRegulacaoTableFilterPopperModel,
} from '../model-regulacao'
import { isFilterTableEqualDefault } from '../utils-regulacao'
import { ListaRegulacaoFilterPopperForm } from './ListaRegulacaoFilterPopperForm'
import { ListaRegulacaoFilterTags } from './ListaRegulacaoFilterTags'
import { ListaRegulacaoSortDropdown } from './ListaRegulacaoSortDropdown'

interface ListaRegulacaoTableFilterProps {
  filter: ListaRegulacaoFilterModel
  updateFilter(filter: ListaRegulacaoFilterModel): void
}

const path = metaPath<ListaRegulacaoTableFilterModel>()

const decorator = createDecorator(...listaRegulacaoTableFilterCalculator)

export function ListaRegulacaoTableFilter(props: Readonly<ListaRegulacaoTableFilterProps>) {
  const { filter, updateFilter } = props

  const theme = useTheme()
  const styles = createStyles(theme)

  const countNotificacaoRegulacao = useTopicoNotificacaoFiltered(TipoTopicoNotificacaoEnum.REGULACAO_ASSISTENCIAL, {
    filtrarPor: 'lido',
  }).length
  const labelSomenteNaoLidos = `Ver somente solicitações com notificação (${
    countNotificacaoRegulacao > MAX_LIMIT_NOTIFICATION_COUNT_DEFAULT
      ? `+${MAX_LIMIT_NOTIFICATION_COUNT_DEFAULT}`
      : countNotificacaoRegulacao
  })`

  const popperFilterCount = useFilterCounter<ListaRegulacaoTableFilterPopperModel>({
    periodo: filter.tableFilter.periodo,
    solicitantes: filter.tableFilter.solicitantes,
  })

  const isFilterDefault = isFilterTableEqualDefault(filter.tableFilter)

  const updateTableFilter = useCallback(
    (tableFilter: ListaRegulacaoTableFilterModel) => {
      updateFilter({ ...filter, tableFilter: { ...filter.tableFilter, ...tableFilter } })
    },
    [filter, updateFilter]
  )

  const handlePopperSubmit = useCallback(
    (values: ListaRegulacaoTableFilterPopperModel) => {
      updateTableFilter({ ...filter.tableFilter, ...values })
    },
    [filter, updateTableFilter]
  )

  const handleDebounceChange = (formState: FormState<ListaRegulacaoTableFilterModel>) => {
    updateTableFilter({ ...filter, ...formState.values, nomeCnsCpf: formState.values.nomeCnsCpf })
  }

  const clearFilter = useCallback(
    () =>
      updateTableFilter({
        ...filter.tableFilter,
        ordem: LISTA_REGULACAO_DEFAULT_FILTERS.tableFilter.ordem,
        nomeCnsCpf: LISTA_REGULACAO_DEFAULT_FILTERS.tableFilter.nomeCnsCpf,
        isSomenteMeusAtendimentos: LISTA_REGULACAO_DEFAULT_FILTERS.tableFilter.isSomenteMeusAtendimentos,
        isSomenteNaoLidos: LISTA_REGULACAO_DEFAULT_FILTERS.tableFilter.isSomenteNaoLidos,
        periodo: LISTA_REGULACAO_DEFAULT_FILTERS.tableFilter.periodo,
        solicitantes: LISTA_REGULACAO_DEFAULT_FILTERS.tableFilter.solicitantes,
      }),
    [filter.tableFilter, updateTableFilter]
  )

  const renderFilterForm = () => {
    return (
      <Fragment>
        <FormDebouncedValueSpy onChange={handleDebounceChange} />
        <HFlow alignItems='center' hSpacing={1} style={styles.container}>
          <TextField
            name={path.nomeCnsCpf}
            placeholder='Pesquise pelo nome, CNS ou CPF'
            icon='zoomOutline'
            onClear={() =>
              updateTableFilter({
                ...filter.tableFilter,
                nomeCnsCpf: LISTA_REGULACAO_DEFAULT_FILTERS.tableFilter.nomeCnsCpf,
              })
            }
            style={styles.textField}
          />
          <HFlow hSpacing={2} justifyContent='space-between' alignItems='center' style={styles.hflowContainer}>
            <HFlow hSpacing={2} alignItems='center' style={styles.leftGroup}>
              <div css={styles.switchWrapper}>
                <SwitchField name={path.isSomenteMeusAtendimentos} label='Ver somente minhas solicitações' />
              </div>
              <div css={styles.switchWrapper}>
                <SwitchField name={path.isSomenteNaoLidos} label={labelSomenteNaoLidos} />
              </div>
            </HFlow>

            <HFlow hSpacing={1} alignItems='center' style={styles.rightGroup}>
              <ListaRegulacaoSortDropdown filter={filter} updateFilter={updateFilter} />
              <PopperButton
                kind='primary'
                size='small'
                skin='default'
                placement='bottom-end'
                renderPopper={renderPopper}
                focusOnOpen={false}
                style={styles.filtroPopper}
              >
                <Icon icon='adjust' style={styles.filterButtonIcon} />
                Filtros ({popperFilterCount})
              </PopperButton>
            </HFlow>
          </HFlow>
        </HFlow>
      </Fragment>
    )
  }

  const renderPopper = (ctrl: PopperControls) => (
    <ListaRegulacaoFilterPopperForm
      initialValues={filter.tableFilter}
      isFilterDefault={isFilterDefault}
      onSubmit={handlePopperSubmit}
      onClear={clearFilter}
      onClose={ctrl.close}
    />
  )

  return (
    <VFlow>
      <Form<ListaRegulacaoTableFilterModel>
        onSubmit={updateTableFilter}
        render={renderFilterForm}
        initialValues={filter.tableFilter}
        decorators={[decorator]}
      />
      <ListaRegulacaoFilterTags filtros={filter.tableFilter} />
    </VFlow>
  )
}

const createStyles = (theme: Theme) => ({
  textField: css`
    flex: 0 0 auto;
    width: 24rem;
  `,
  filterButtonIcon: css`
    margin-right: 0.5rem;
  `,
  container: css`
    display: flex;
    flex-wrap: wrap;
  `,
  hflowContainer: css`
    flex: 1 1 68rem;
    flex-wrap: nowrap;
  `,
  switchWrapper: css`
    & label span {
      flex: 1;
    }
  `,
  leftGroup: css`
    ${theme.breakpoints.down('lg')} {
      gap: 1rem;
    }
  `,
  rightGroup: css`
    ${theme.breakpoints.down('lg')} {
      gap: 0.5rem;
    }
  `,
  filtroPopper: css`
    white-space: nowrap;
  `,
})
